import * as yup from 'yup';
import { Row, RenderConfig, Column, EMPTY_ARRAY, NumberInput } from 'commons-ui';
import { onChangeConfigValAction } from './helper';

const thresholdValidation = (prefix: string) =>
	yup.object().shape({
		[`${prefix}-low`]: yup
			.number()
			.required('Low is a required field')
			.max(100, 'Low must be less than 100')
			.test('is-greater-than-poor', 'Low must be greater than Poor', function checkLowGreaterThanPoor(value) {
				const poor = this.parent[`${prefix}-poor`];
				return value > poor;
			}),
		[`${prefix}-poor`]: yup
			.number()
			.required('Poor is a required field')
			.test('is-greater-than-critical', 'Poor must be greater than Critical', function checkPoorGreaterThanCritical(value) {
				const critical = this.parent[`${prefix}-critical`];
				return value > critical;
			}),
		[`${prefix}-critical`]: yup.number().required('Critical is a required field').min(1, 'Critical must be greater than 0')
	});

export const validationSchemaEdit = yup.object().shape({
	defaultDowntimeMeter: thresholdValidation('defaultDowntimeMeter'),
	bank: thresholdValidation('bank'),
	instrument: thresholdValidation('instrument'),
	handle: thresholdValidation('handle')
});

export const FORM_FIELD_KEYS = {
	DEFAULT_DOWNTIME_METER: 'defaultDowntimeMeter',
	DEFAULT_DOWNTIME_METER_LOW: 'defaultDowntimeMeter-low',
	DEFAULT_DOWNTIME_METER_POOR: 'defaultDowntimeMeter-poor',
	DEFAULT_DOWNTIME_METER_CRITICAL: 'defaultDowntimeMeter-critical',
	BANK: 'bank',
	BANK_LOW: 'bank-low',
	BANK_POOR: 'bank-poor',
	BANK_CRITICAL: 'bank-critical',
	INSTRUMENT: 'instrument',
	INSTRUMENT_LOW: 'instrument-low',
	INSTRUMENT_POOR: 'instrument-poor',
	INSTRUMENT_CRITICAL: 'instrument-critical',
	HANDLE: 'handle',
	HANDLE_LOW: 'handle-low',
	HANDLE_POOR: 'handle-poor',
	HANDLE_CRITICAL: 'handle-critical'
};

export const FORM_LAYOUT_CONFIG = [
	new Row(FORM_FIELD_KEYS.DEFAULT_DOWNTIME_METER, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.DEFAULT_DOWNTIME_METER_CRITICAL)]),
		new Column([new Row(FORM_FIELD_KEYS.DEFAULT_DOWNTIME_METER_POOR)]),
		new Column([new Row(FORM_FIELD_KEYS.DEFAULT_DOWNTIME_METER_LOW)])
	]),
	new Row(FORM_FIELD_KEYS.BANK, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.BANK_CRITICAL)]),
		new Column([new Row(FORM_FIELD_KEYS.BANK_POOR)]),
		new Column([new Row(FORM_FIELD_KEYS.BANK_LOW)])
	]),
	new Row(FORM_FIELD_KEYS.INSTRUMENT, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.INSTRUMENT_CRITICAL)]),
		new Column([new Row(FORM_FIELD_KEYS.INSTRUMENT_POOR)]),
		new Column([new Row(FORM_FIELD_KEYS.INSTRUMENT_LOW)])
	]),
	new Row(FORM_FIELD_KEYS.HANDLE, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.HANDLE_CRITICAL)]),
		new Column([new Row(FORM_FIELD_KEYS.HANDLE_POOR)]),
		new Column([new Row(FORM_FIELD_KEYS.HANDLE_LOW)])
	])
];

export const FORM_FIELD_CONFIG = {
	[FORM_FIELD_KEYS.DEFAULT_DOWNTIME_METER]: new RenderConfig()
		.setName(FORM_FIELD_KEYS.DEFAULT_DOWNTIME_METER)
		.setLabel('Default Downtime Meter')
		.setRenderProps({
			isArrayField: false,
			isEditable: { create: true, edit: true },
			required: false
		}),
	[FORM_FIELD_KEYS.DEFAULT_DOWNTIME_METER_LOW]: new RenderConfig()
		.setName(FORM_FIELD_KEYS.DEFAULT_DOWNTIME_METER_LOW)
		.setLabel('Low')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: false,
			onChangeCallback: onChangeConfigValAction
		}),
	[FORM_FIELD_KEYS.DEFAULT_DOWNTIME_METER_POOR]: new RenderConfig()
		.setName(FORM_FIELD_KEYS.DEFAULT_DOWNTIME_METER_POOR)
		.setLabel('Poor')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: false,
			onChangeCallback: onChangeConfigValAction
		}),
	[FORM_FIELD_KEYS.DEFAULT_DOWNTIME_METER_CRITICAL]: new RenderConfig()
		.setName(FORM_FIELD_KEYS.DEFAULT_DOWNTIME_METER_CRITICAL)
		.setLabel('Critical')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: false,
			onChangeCallback: onChangeConfigValAction
		}),
	[FORM_FIELD_KEYS.BANK]: new RenderConfig()
		.setName(FORM_FIELD_KEYS.BANK)
		.setLabel('Bank Downtime Meter')
		.setRenderProps({
			isArrayField: false,
			isEditable: { create: true, edit: true },
			required: false
		}),
	[FORM_FIELD_KEYS.BANK_LOW]: new RenderConfig()
		.setName(FORM_FIELD_KEYS.BANK_LOW)
		.setLabel('Low')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: false,
			onChangeCallback: onChangeConfigValAction
		}),
	[FORM_FIELD_KEYS.BANK_POOR]: new RenderConfig()
		.setName(FORM_FIELD_KEYS.BANK_POOR)
		.setLabel('Poor')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: false,
			onChangeCallback: onChangeConfigValAction
		}),
	[FORM_FIELD_KEYS.BANK_CRITICAL]: new RenderConfig()
		.setName(FORM_FIELD_KEYS.BANK_CRITICAL)
		.setLabel('Critical')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: false,
			onChangeCallback: onChangeConfigValAction
		}),
	[FORM_FIELD_KEYS.INSTRUMENT]: new RenderConfig()
		.setName(FORM_FIELD_KEYS.INSTRUMENT)
		.setLabel('PSP Downtime Meter')
		.setRenderProps({
			isArrayField: false,
			isEditable: { create: true, edit: true },
			required: false
		}),
	[FORM_FIELD_KEYS.INSTRUMENT_LOW]: new RenderConfig()
		.setName(FORM_FIELD_KEYS.INSTRUMENT_LOW)
		.setLabel('Low')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: false,
			onChangeCallback: onChangeConfigValAction
		}),
	[FORM_FIELD_KEYS.INSTRUMENT_POOR]: new RenderConfig()
		.setName(FORM_FIELD_KEYS.INSTRUMENT_POOR)
		.setLabel('Poor')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: false,
			onChangeCallback: onChangeConfigValAction
		}),
	[FORM_FIELD_KEYS.INSTRUMENT_CRITICAL]: new RenderConfig()
		.setName(FORM_FIELD_KEYS.INSTRUMENT_CRITICAL)
		.setLabel('Critical')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: false,
			onChangeCallback: onChangeConfigValAction
		}),
	[FORM_FIELD_KEYS.HANDLE]: new RenderConfig()
		.setName(FORM_FIELD_KEYS.HANDLE)
		.setLabel('Handle Downtime Meter')
		.setRenderProps({
			isArrayField: false,
			isEditable: { create: true, edit: true },
			required: false
		}),
	[FORM_FIELD_KEYS.HANDLE_LOW]: new RenderConfig()
		.setName(FORM_FIELD_KEYS.HANDLE_LOW)
		.setLabel('Low')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: false,
			onChangeCallback: onChangeConfigValAction
		}),
	[FORM_FIELD_KEYS.HANDLE_POOR]: new RenderConfig()
		.setName(FORM_FIELD_KEYS.HANDLE_POOR)
		.setLabel('Poor')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: false,
			onChangeCallback: onChangeConfigValAction
		}),
	[FORM_FIELD_KEYS.HANDLE_CRITICAL]: new RenderConfig()
		.setName(FORM_FIELD_KEYS.HANDLE_CRITICAL)
		.setLabel('Critical')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: false,
			onChangeCallback: onChangeConfigValAction
		})
};
